/**
 * Modal para agregar/editar detalles de residuos
 * Migrado del módulo legacy de residuos con toda su funcionalidad
 * 
 * Campos completos:
 * - Categoría de Residuo (filtrada por tipo de residuo y destino final)
 * - Residuo (filtrado por categoría)
 * - Unidad de Medida
 * - Cantidad
 * - Kg. Guía (auto-calculado)
 * - Kg. Limpiezas de Playa (integración beach cleaning)
 * - Total Kilogramos (suma de ambos)
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Row,
	Col,
	Label,
	Input,
	FormFeedback,
	Card,
	CardBody,
	Alert,
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import OSelect from '../../../../../Components/OElements/OSelect';

// Helpers
import {
	getResidueCategoryList,
	getResidueList,
	getUnitMeasurementList,
	getResidueConversionList,
} from '../../../../../helpers/residue/backend_helper';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import { useFormDictionary } from '../FormDictionaryContext';
import {
	parsePeriodStringToRange,
	serializePeriodRange,
} from '../../utils/periodRangeString';
import { getMortalityFieldVisibility } from '../../utils/mortalityFieldVisibility';

export const ResiduosSelectorModal = ({
	isOpen,
	toggle,
	onAdd,
	onUpdate,
	residueTypeId,
	residueCategoryId = null,
	finalDestinationId = null,
	editingDetail = null,
	guideDetailsList = [],
	establishmentTypeId = '',
}) => {
	const isEdit = !!editingDetail;

	// Acceder a maestros del diccionario global
	const { species, cultivationCenters } = useFormDictionary();

	// Preparar opciones para selects
	const speciesOptions = useMemo(
		() =>
			species.map(s => ({
				value: s.name,
				label: s.name,
				item: s,
			})),
		[species]
	);

	const cultivationCenterOptions = useMemo(
		() =>
			cultivationCenters.map(cc => ({
				value: cc.name,
				label: cc.name,
				siep: cc.code_siep,
				item: cc,
			})),
		[cultivationCenters]
	);

	// Handler para seleccionar centro de cultivo (auto-completa SIEP)
	const handleCultivationCenterSelect = center => {
		validation.setFieldValue('cultivation_center_origin', center?.name || '');
		validation.setFieldValue(
			'siep_code_cultivation_center',
			center?.code_siep || ''
		);
	};

	const [selectedResidueCategory, setSelectedResidueCategory] = useState(null);
	const [selectedResidue, setSelectedResidue] = useState(null);
	const [selectedUnitMeasurement, setSelectedUnitMeasurement] = useState(null);
	const [conversion, setConversion] = useState(null);
	const [loadingConversion, setLoadingConversion] = useState(false);
	// Refs para evitar loops y fetches redundantes
	const lastConversionKeyRef = useRef('');
	const lastEditDetailRef = useRef(null);

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			residue_category_id: editingDetail?.residue_category_id || '',
			residue_id: editingDetail?.residue_id || '',
			unit_measurement_id: editingDetail?.unit_measurement_id || '',
			amount: editingDetail?.amount || '',
			residue_kilograms: editingDetail?.residue_kilograms || '',
			residue_collected_kilograms:
				editingDetail?.residue_collected_kilograms || 0,
			kilograms_total: editingDetail?.kilograms_total || '',
			// NUEVOS: Campos específicos para residuos de mortalidad
			ph_ensilage: editingDetail?.ph_ensilage || '',
			hydrogen_sulfide: editingDetail?.hydrogen_sulfide || '',
			number_of_bins: editingDetail?.number_of_bins || '',
			// NUEVOS: Campos específicos para mortalidad ensilada
			especie: editingDetail?.especie || '',
			fish_count: editingDetail?.fish_count || '',
			cubic_meters: editingDetail?.cubic_meters || '',
			has_flow_meter: editingDetail?.has_flow_meter || false,
			period: editingDetail?.period || '',
			// NUEVOS: Campos específicos para mortalidad peces enteros
			cultivation_center_origin: editingDetail?.cultivation_center_origin || '',
			siep_code_cultivation_center: editingDetail?.siep_code_cultivation_center || '',
			cage_origin: editingDetail?.cage_origin || '',
			bin_seal_folios: editingDetail?.bin_seal_folios || '',
			// NUEVOS: Campos específicos para Vísceras
			market_restrictions: editingDetail?.market_restrictions || '',
		},
		validationSchema: Yup.object({
			residue_category_id: Yup.string().required('Campo requerido'),
			residue_id: Yup.string().required('Campo requerido'),
			unit_measurement_id: Yup.string().required('Campo requerido'),
			amount: Yup.number()
				.required('Campo requerido')
				.min(0.01, 'La cantidad debe ser mayor a 0'),
		}),
		onSubmit: values => {
			const detailData = {
				residue_category_id: values.residue_category_id,
				residue_id: values.residue_id,
				residue: selectedResidue || editingDetail?.residue,
				unit_measurement_id: values.unit_measurement_id,
				unit_measurement:
					selectedUnitMeasurement || editingDetail?.unit_measurement,
				amount: values.amount,
				residue_kilograms: values.residue_kilograms,
				residue_collected_kilograms: editingDetail?.residue_collected_kilograms || 0,
				kilograms_total: values.kilograms_total,
				residue_collected_ids: editingDetail?.residue_collected_ids || [],
				// NUEVOS: Campos específicos para residuos de mortalidad
				ph_ensilage: values.ph_ensilage || null,
				hydrogen_sulfide: values.hydrogen_sulfide || null,
				number_of_bins: values.number_of_bins || null,
				// NUEVOS: Campos específicos para mortalidad ensilada
				especie: values.especie || null,
				fish_count: values.fish_count || null,
				cubic_meters: values.cubic_meters || null,
				has_flow_meter: values.has_flow_meter || null,
				period: values.period || null,
				// NUEVOS: Campos específicos para mortalidad peces enteros
				cultivation_center_origin: values.cultivation_center_origin || null,
				siep_code_cultivation_center: values.siep_code_cultivation_center || null,
				cage_origin: values.cage_origin || null,
				bin_seal_folios: values.bin_seal_folios || null,
				// NUEVOS: Campos específicos para Vísceras
				market_restrictions: values.market_restrictions || null,
				// NUEVOS: Campos de valorización desde catálogo
				es_valorizable: selectedResidue?.es_valorizable || false,
				valor: selectedResidue?.valor || null,
				moneda: selectedResidue?.moneda || null,
			};

			// Alertar si es valorizable
			if (selectedResidue?.es_valorizable) {
				toast.info(
					'Este residuo está valorizado. La guía cambiará a "Operación constituye venta".',
					{ autoClose: 5000 }
				);
			}

			if (isEdit) {
				// Verificar duplicado al editar
				const duplicate = guideDetailsList.find(
					detail =>
						detail.residue_id === detailData.residue_id &&
						detail !== editingDetail
				);
				if (duplicate) {
					toast.error(
						`Ya existe un detalle con el residuo "${detailData.residue.name}".`,
						{ autoClose: 3000 }
					);
					return;
				}
				onUpdate(detailData);
				toast.success(
					`Residuo "${detailData.residue.name}" actualizado exitosamente.`,
					{ autoClose: 3000 }
				);
			} else {
				// Verificar duplicado al agregar
				const duplicate = guideDetailsList.find(
					detail => detail.residue_id === detailData.residue_id
				);
				if (duplicate) {
					toast.error(
						`Ya existe un detalle con el residuo "${detailData.residue.name}".`,
						{ autoClose: 3000 }
					);
					return;
				}
				onAdd(detailData);
				toast.success(
					`Residuo "${detailData.residue.name}" agregado exitosamente.`,
					{ autoClose: 3000 }
				);
			}

			handleClose();
		},
	});

	// Calcular automáticamente residue_kilograms y kilograms_total con conversión
	// Usa valores primitivos como deps para evitar re-ejecuciones innecesarias
	const amount = validation.values.amount;
	const unitCode = selectedUnitMeasurement?.code;
	useEffect(() => {
		if (!amount || !selectedUnitMeasurement) return;

		const numAmount = Number(amount);
		if (!numAmount || !isFinite(numAmount)) return;

		let residueKg;

		if (conversion) {
			// Aplicar conversión configurada
			residueKg = numAmount * conversion.kilogram;
		} else if (unitCode === 'KG') {
			// Si es KG, usar directo
			residueKg = numAmount;
		} else {
			// Sin conversión y no es KG: asumir 1:1
			residueKg = numAmount;
		}

		const collectedKg = Number(
			editingDetail?.residue_collected_kilograms || 0
		);
		const totalKg = residueKg + collectedKg;

		// Solo actualizar si el valor realmente cambió (evita cascada de renders)
		if (validation.values.residue_kilograms !== residueKg) {
			validation.setFieldValue('residue_kilograms', residueKg, false);
		}
		if (validation.values.kilograms_total !== totalKg) {
			validation.setFieldValue('kilograms_total', totalKg, false);
		}
	}, [amount, conversion, unitCode]);

	// Buscar conversión cuando cambien residue_id o unit_measurement_id
	// Usa ref para evitar fetch duplicado con la misma combinación
	const residueIdVal = validation.values.residue_id;
	const unitMeasurementIdVal = validation.values.unit_measurement_id;
	useEffect(() => {
		if (!residueIdVal || !unitMeasurementIdVal) return;

		const conversionKey = `${residueIdVal}_${unitMeasurementIdVal}`;
		if (lastConversionKeyRef.current === conversionKey) return;
		lastConversionKeyRef.current = conversionKey;

		let cancelled = false;
		const fetchConversion = async () => {
			setLoadingConversion(true);
			try {
				const response = await getResidueConversionList({
					residue_id: residueIdVal,
					unit_measurement_id: unitMeasurementIdVal,
				});

				if (cancelled) return;

				if (response && response.length > 0) {
					setConversion(response[0]);
				} else {
					setConversion(null);
				}
			} catch (error) {
				if (!cancelled) {
					console.error('Error al buscar conversión:', error);
					setConversion(null);
				}
			} finally {
				if (!cancelled) {
					setLoadingConversion(false);
				}
			}
		};

		fetchConversion();
		return () => { cancelled = true; };
	}, [residueIdVal, unitMeasurementIdVal]);

	// Cargar datos al editar O auto-seleccionar categoría si está forzada
	useEffect(() => {
		if (isEdit && editingDetail) {
			// Evitar reprocesar el mismo editingDetail
			if (lastEditDetailRef.current === editingDetail) return;
			lastEditDetailRef.current = editingDetail;

			setSelectedResidueCategory(editingDetail.residue?.residue_category || null);
			setSelectedResidue(editingDetail.residue || null);
			setSelectedUnitMeasurement(editingDetail.unit_measurement || null);
		} else if (residueCategoryId && !isEdit) {
			// Auto-seleccionar categoría para "Limpieza de Playa"
			if (validation.values.residue_category_id !== residueCategoryId) {
				validation.setFieldValue('residue_category_id', residueCategoryId, false);
			}
		}
	}, [isEdit, editingDetail, residueCategoryId]);

	const handleClose = () => {
		validation.resetForm();
		setSelectedResidueCategory(null);
		setSelectedResidue(null);
		setSelectedUnitMeasurement(null);
		setConversion(null);
		setLoadingConversion(false);
		lastConversionKeyRef.current = '';
		lastEditDetailRef.current = null;
		toggle();
	};

	const {
		showEnsilageFields,
		showViscerasFields,
		showFishCount,
		showNumberOfBins,
		showBinSealFolios,
		showCultivationCenterOrigin,
		showCageOrigin,
		isPecesEnterosPlanta,
		isPecesEnteroCentro,
		isPecesEnterosIneligible,
	} = getMortalityFieldVisibility(
		validation.values.residue_id,
		establishmentTypeId
	);
	const showMortalitySection =
		showEnsilageFields || showCageOrigin || showViscerasFields;

	const periodRangePickerValue = useMemo(
		() => parsePeriodStringToRange(validation.values.period || ''),
		[validation.values.period],
	);

	return (
		<Modal isOpen={isOpen} toggle={handleClose} size='lg'>
			<ModalHeader toggle={handleClose}>
				{isEdit ? 'Editar' : 'Agregar'} Detalle de Residuo
			</ModalHeader>
			<ModalBody>
				<Row className='g-3'>
					<Col lg={6}>
						<OSelect
							key={`residue_category_${editingDetail?.residue?.residue_category_id || 'new'}_${residueTypeId || 'none'}_${residueCategoryId || 'none'}_${finalDestinationId || 'none'}`}
							label='Categoría de Residuo'
							id='residue_category_id'
							name='residue_category_id'
							value={editingDetail?.residue?.residue_category_id || residueCategoryId || ''}
							onSelect={setSelectedResidueCategory}
							requestApi={() =>
								getResidueCategoryList({
									residue_type_id: residueTypeId || '',
									final_destination_id: finalDestinationId,
									...(residueCategoryId && { _id: residueCategoryId }), // Filtrar por categoría específica si existe
								})
							}
							disabled={isEdit || !!residueCategoryId} // Deshabilitar si hay categoría forzada
							validation={validation}
						/>
					</Col>
					<Col lg={6}>
						<OSelect
							key={`residue_${editingDetail?.residue?._id || 'new'}_${validation.values.residue_category_id || 'none'}`}
							label='Residuo'
							id='residue_id'
							name='residue_id'
							value={editingDetail?.residue?._id || ''}
							onSelect={setSelectedResidue}
							requestApi={() =>
								getResidueList({
									residue_category_id: validation.values.residue_category_id,
								})
							}
							disabled={
								validation.values.residue_category_id === '' || isEdit
							}
							validation={validation}
						/>
					</Col>
					<Col lg={4}>
						<OSelect
							key={`unit_${editingDetail?.unit_measurement?._id}`}
							label='Unidad de Medida'
							id='unit_measurement_id'
							name='unit_measurement_id'
							value={editingDetail?.unit_measurement?._id || ''}
							onSelect={setSelectedUnitMeasurement}
							requestApi={() => getUnitMeasurementList()}
							validation={validation}
						/>
					</Col>
					<Col lg={4}>
						<Label for='amount' className='form-label'>
							Cantidad
						</Label>
						<Input
							name='amount'
							id='amount'
							className='form-control'
							placeholder='Ingrese una cantidad'
							type='number'
							step='0.01'
							min='0'
							onChange={validation.handleChange}
							onBlur={validation.handleBlur}
							value={validation.values.amount || ''}
							invalid={
								validation.touched.amount && validation.errors.amount
									? true
									: false
							}
						/>
						{validation.touched.amount && validation.errors.amount ? (
							<FormFeedback type='invalid'>
								{validation.errors.amount}
							</FormFeedback>
						) : null}
					</Col>
					<Col lg={4}>
						<Label for='residue_kilograms' className='form-label'>
							Kilogramos
						</Label>
						<Input
							name='residue_kilograms'
							id='residue_kilograms'
							className='form-control bg-light'
							type='text'
							disabled
							value={`${validation.values.residue_kilograms || 0} KG`}
						/>
						{selectedUnitMeasurement && validation.values.amount && (
							<small className='text-muted'>
								{validation.values.amount} {selectedUnitMeasurement.name}
								{conversion && ` × ${conversion.kilogram} KG`}
								{!conversion &&
									selectedUnitMeasurement.code !== 'KG' &&
									' (1:1)'}
							</small>
						)}
					</Col>

					{/* Indicador de conversión */}
					{conversion && selectedUnitMeasurement && (
						<Col lg={12}>
							<Alert color='info' className='mb-0'>
								<i className='ri-information-line me-1'></i>
								<strong>Conversión aplicada:</strong> 1{' '}
								{selectedUnitMeasurement.name} = {conversion.kilogram} KG
							</Alert>
						</Col>
					)}

					{!conversion &&
						selectedUnitMeasurement &&
						selectedUnitMeasurement.code !== 'KG' &&
						!loadingConversion && (
							<Col lg={12}>
								<Alert color='warning' className='mb-0'>
									<i className='ri-alert-line me-1'></i>
									No hay conversión configurada para esta unidad. Se
									asumirá relación 1:1.
								</Alert>
							</Col>
						)}

					{/* Desglose de kilogramos */}
					<Col lg={12}>
						<Card className='border mb-0 bg-light'>
							<CardBody className='p-3'>
								<Row className='g-3'>
									<Col lg={4}>
										<Label for='residue_kilograms_detail' className='form-label'>
											Kg. Guía
										</Label>
										<Input
											name='residue_kilograms_detail'
											id='residue_kilograms_detail'
											className='form-control'
											type='number'
											disabled
											value={validation.values.residue_kilograms || ''}
										/>
									</Col>
									<Col lg={4}>
										<Label
											for='residue_collected_kilograms'
											className='form-label'
										>
											Kg. Limpiezas De Playa
										</Label>
										<Input
											name='residue_collected_kilograms'
											id='residue_collected_kilograms'
											className='form-control'
											type='number'
											disabled
											value={
												validation.values.residue_collected_kilograms || 0
											}
										/>
									</Col>
									<Col lg={4}>
										<Label for='kilograms_total' className='form-label'>
											<strong>Total Kilogramos</strong>
										</Label>
										<Input
											name='kilograms_total'
											id='kilograms_total'
											className='form-control fw-bold'
											type='number'
											disabled
											value={validation.values.kilograms_total || ''}
										/>
									</Col>
								</Row>
							</CardBody>
						</Card>
					</Col>

					{/* Alerta: PECES_ENTEROS seleccionado pero establecimiento no califica */}
					{isPecesEnterosIneligible && (
						<Col lg={12}>
							<Alert color='danger' className='mb-0'>
								<i className='ri-error-warning-line me-2'></i>
								<strong>Mortalidad Entera</strong> solo está disponible cuando el establecimiento
								de origen es una <strong>Planta Primaria</strong> o un <strong>Centro de Cultivo</strong>.
								Cambie el establecimiento o seleccione otro residuo.
							</Alert>
						</Col>
					)}

					{/* Campos específicos para residuos de mortalidad */}
					{showMortalitySection && !isPecesEnterosIneligible && (
						<Col lg={12}>
							<Card className='border border-primary mb-0'>
								<CardBody className='p-3'>
									<div className='d-flex align-items-center mb-3'>
										<i className='ri-heart-pulse-line text-primary me-2 fs-5'></i>
										<h6 className='mb-0 text-primary'>Datos específicos de Mortalidad</h6>
									</div>
									{isPecesEnterosPlanta && (
										<Alert color='warning' className='mb-3 py-2'>
											<i className='ri-building-line me-2'></i>
											<strong>Despacho desde Planta Primaria a Reductora</strong>
										</Alert>
									)}
									{isPecesEnteroCentro && (
										<Alert color='info' className='mb-3 py-2'>
											<i className='ri-flood-line me-2'></i>
											<strong>Contingencia desde Centro de Cultivo a Reductora</strong>
										</Alert>
									)}
									<Row className='g-3'>
										{/* Especie — todas las mortalidades */}
										<Col lg={6}>
											<OSelect
												label='Especie'
												id='especie'
												name='especie'
												value={validation.values.especie || ''}
												onSelect={item =>
													validation.setFieldValue(
														'especie',
														item?.name || '',
													)
												}
												itemsSelect={speciesOptions}
												validation={validation}
												placeholder='Seleccione especie...'
											/>
										</Col>
										{/* Fish count — ensilada + peces enteros (ambas variantes) */}
										{showFishCount && (
											<Col lg={6}>
												<Label for='fish_count' className='form-label'>
													Nro. de peces
												</Label>
												<Input
													name='fish_count'
													id='fish_count'
													className='form-control'
													placeholder='Ingrese número de peces'
													type='number'
													min='0'
													onChange={validation.handleChange}
													onBlur={validation.handleBlur}
													value={validation.values.fish_count || ''}
												/>
											</Col>
										)}
										{/* N° de Bins — ensilada + vísceras + peces enteros CC */}
										{showNumberOfBins && (
											<Col lg={6}>
												<Label for='number_of_bins' className='form-label'>
													N° de Bins
												</Label>
												<Input
													name='number_of_bins'
													id='number_of_bins'
													className='form-control'
													placeholder='Ingrese número de bins'
													type='number'
													min='0'
													onChange={validation.handleChange}
													onBlur={validation.handleBlur}
													value={validation.values.number_of_bins || ''}
												/>
											</Col>
										)}
										{/* Ensilada: m³, pH, ácido, flujómetro, periodo */}
										{showEnsilageFields && (
											<>
												<Col lg={6}>
													<Label for='cubic_meters' className='form-label'>
														Metros Cúbicos (m³)
													</Label>
													<Input
														name='cubic_meters'
														id='cubic_meters'
														className='form-control'
														placeholder='Ingrese metros cúbicos'
														type='number'
														step='0.01'
														min='0'
														onChange={validation.handleChange}
														onBlur={validation.handleBlur}
														value={validation.values.cubic_meters || ''}
													/>
												</Col>
												<Col lg={6}>
													<Label for='ph_ensilage' className='form-label'>
														pH en caso de ensilaje
													</Label>
													<Input
														name='ph_ensilage'
														id='ph_ensilage'
														className='form-control'
														placeholder='Ingrese el pH'
														type='number'
														step='0.1'
														min='0'
														max='14'
														onChange={validation.handleChange}
														onBlur={validation.handleBlur}
														value={validation.values.ph_ensilage || ''}
													/>
												</Col>
													{/* TODO: Campo Ácido sulfhídrico deshabilitado temporalmente.
														     Se conserva en backend (models) para uso futuro en la industria de mortalidad ensilada.
														<Col lg={6}>
															<Label for='hydrogen_sulfide' className='form-label'>
																Ácido sulfhídrico
															</Label>
															<Input
																name='hydrogen_sulfide'
																id='hydrogen_sulfide'
																className='form-control'
																placeholder='Ingrese ácido sulfhídrico'
																type='number'
																step='0.01'
																min='0'
																onChange={validation.handleChange}
																onBlur={validation.handleBlur}
																value={validation.values.hydrogen_sulfide || ''}
															/>
														</Col>
													*/}
												<Col lg={6}>
													<div className='form-check form-switch mt-4 pt-2'>
														<Input
															name='has_flow_meter'
															id='has_flow_meter'
															type='switch'
															checked={
																validation.values.has_flow_meter || false
															}
															onChange={validation.handleChange}
														/>
														<Label
															for='has_flow_meter'
															className='form-check-label ms-2'
														>
															¿Dispone de flujómetro?
														</Label>
													</div>
												</Col>
												<Col lg={12}>
													<DatesProvider
														settings={{
															locale: 'es-mx',
															firstDayOfWeek: 1,
															weekendDays: [0],
															timezone: 'UTC',
														}}
													>
														<DatePickerInput
															type='range'
															label='Periodo que abarca'
															id='period'
															name='period'
															placeholder='Seleccione fecha inicio y fin'
															value={periodRangePickerValue}
															onChange={range => {
																const tuple = Array.isArray(range)
																	? range
																	: [null, null];
																validation.setFieldValue(
																	'period',
																	serializePeriodRange(tuple),
																	false,
																);
															}}
															onBlur={() =>
																validation.setFieldTouched('period', true)
															}
															valueFormat='DD/MM/YYYY'
															maxDate={new Date()}
															clearable
														/>
													</DatesProvider>
												</Col>
											</>
										)}
										{/* Centro cultivo origen — Planta Primaria + Vísceras */}
										{showCultivationCenterOrigin && (
											<>
												<Col lg={6}>
													<OSelect
														label='Centro cultivo origen'
														id='cultivation_center_origin'
														name='cultivation_center_origin'
														value={
															validation.values
																.cultivation_center_origin || ''
														}
														onSelect={handleCultivationCenterSelect}
														itemsSelect={cultivationCenterOptions}
														validation={validation}
														placeholder='Seleccione centro...'
													/>
												</Col>
												<Col lg={6}>
													<Label
														for='siep_code_cultivation_center'
														className='form-label'
													>
														Código SIEP centro cultivo
													</Label>
													<Input
														name='siep_code_cultivation_center'
														id='siep_code_cultivation_center'
														className='form-control bg-light'
														placeholder='Se completa automáticamente'
														type='text'
														disabled
														readOnly
														value={
															validation.values
																.siep_code_cultivation_center || ''
														}
													/>
												</Col>
											</>
										)}
										{/* Jaula de origen — Peces enteros (ambas variantes) + Vísceras */}
										{showCageOrigin && (
											<Col lg={6}>
												<Label for='cage_origin' className='form-label'>
													Jaula de origen
												</Label>
												<Input
													name='cage_origin'
													id='cage_origin'
													className='form-control'
													placeholder='Ej: 103-106-108-102'
													type='text'
													onChange={validation.handleChange}
													onBlur={validation.handleBlur}
													value={validation.values.cage_origin || ''}
												/>
											</Col>
										)}
										{/* Folios sellos bins — solo Peces enteros CC */}
										{showBinSealFolios && (
											<Col lg={6}>
												<Label for='bin_seal_folios' className='form-label'>
													Folios de sellos de bins
												</Label>
												<Input
													name='bin_seal_folios'
													id='bin_seal_folios'
													className='form-control'
													placeholder='Ej: 1234-5678-9012'
													type='text'
													onChange={validation.handleChange}
													onBlur={validation.handleBlur}
													value={validation.values.bin_seal_folios || ''}
												/>
											</Col>
										)}
										{/* Restricciones de mercado — solo Vísceras */}
										{showViscerasFields && (
											<Col lg={12}>
												<Label for='market_restrictions' className='form-label'>
													Restricciones de mercado
												</Label>
												<Input
													name='market_restrictions'
													id='market_restrictions'
													className='form-control'
													placeholder='Ej: Solo para exportación, no para consumo local'
													type='text'
													onChange={validation.handleChange}
													onBlur={validation.handleBlur}
													value={validation.values.market_restrictions || ''}
												/>
											</Col>
										)}
									</Row>
									</CardBody>
								</Card>
						</Col>
					)}
				</Row>
			</ModalBody>
			<ModalFooter>
				<div className='hstack gap-2'>
					<Button type='button' onClick={handleClose} className='btn-danger'>
						<i className='ri-close-line me-1'></i>
						Cancelar
					</Button>
					<Button
						type='button'
						color='primary'
						disabled={isPecesEnterosIneligible}
						onClick={() => validation.handleSubmit()}
					>
						<i className='ri-save-line me-1'></i>
						{isEdit ? 'Actualizar' : 'Guardar'}
					</Button>
				</div>
			</ModalFooter>
		</Modal>
	);
};
